<template>
  <div class="flex w-full relative">
    <button @click.prevent="toggle" class="button tertiary hover-rectangle" :class="{'bg-gray-100 text-black hover:bg-gray-200 hover:text-black': isActive }">
      <i :class="iconClasses" class="mr-0"></i>
      <span class="sr-only">Bookmark</span>
    </button>

    <HidablePopover ref="popover" class="popover w-2xs @sm:w-xs" data-controller="autoposition" :data-autoposition-force-right-value="forceRight" :dismissable="false">
      <form 
        :action="bookmarked ? `/my/bookmarks/${userBookmarkId}` : '/my/bookmarks'" 
        :id="`bookmark-dropdown-${targetId}`" 
        @submit.prevent="updateBookmark" 
        method="post" 
        class="bg-gray-50 rounded-md stacked"
      >
        <input v-if="bookmarked" type="hidden" name="_method" value="patch" />
        <input type="hidden" name="user_bookmark[target_id]" :value="targetId" />
        <input type="hidden" name="user_bookmark[program_id]" :value="programId" />

        <div class="form-group">
          <div class="form-row">
            <label class="leading-6 py-1.5" for="user_bookmark_notes_attributes_0_body">Notes (Optional)</label>
            <div class="controls">
              <textarea
                rows="5"
                placeholder="Enter your notes here."
                name="user_bookmark[notes_attributes][0][body]"
                id="user_bookmark_notes_attributes_0_body"
                v-model="noteValue"
                ref="noteTextarea"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Admin Options -->
        <div class="px-6">
          <div v-if="currentUserIsAdmin" class="label-container flex space-x-4">
            <div class="hover-rectangle flex items-center">
              <input name="user_bookmark[admin_visible]" type="hidden" value="0" autocomplete="off">
              <input type="checkbox" value="1" checked="checked" name="user_bookmark[admin_visible]" id="user_bookmark_admin_visible">
            </div>
            <label class="leading-6 pt-1.5" for="user_bookmark_admin_visible">Visible to all administrators</label>
          </div>

          <div v-if="currentUserIsAdmin" class="label-container flex space-x-4">
            <div class="hover-rectangle flex items-center">
              <input name="user_bookmark[admin_editable]" type="hidden" value="0" autocomplete="off">
              <input type="checkbox" value="1" checked="checked" name="user_bookmark[admin_editable]" id="user_bookmark_admin_editable">
            </div>
            <label class="leading-6 pt-1.5" for="user_bookmark_admin_editable">Editable by other administrators</label>
          </div>
        </div>

        <!-- Submit and Remove Bookmark -->
        <div class="submit-button-container !pt-0 !bg-transparent border-none">
          <button
            v-if="bookmarked"
            type="button"
            class="button secondary m-0 h-10"
            @click.prevent="deleteBookmark"
            :data-confirm="noteValue ? 'Notes will be deleted. Are you sure you want to remove the bookmark?' : null" 
            aria-label="Remove bookmark"
          >
            {{ props.removeButtonText }}
          </button>

          <button type="submit" name="commit" class="button cursor-pointer" aria-label="Save bookmark">
            {{ props.saveButtonText }}
          </button>
        </div>
      </form>
    </HidablePopover>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from "vue";
  import HidablePopover from './HidablePopover.vue'

  const props = defineProps({
    bookmarked:         { type: Boolean, required: true },
    currentUserIsAdmin: { type: Boolean, default: false },
    targetId:           { type: [Number, String], required: true },
    programId:          { type: [Number, String], required: true },
    userBookmarkId:     { type: [Number, String], default: null },
    note:               { type: String, default: '' },
    saveButtonText:     { type: String, default: 'Save'},
    removeButtonText:   { type: String, default: 'Remove'},
    forceRight:         { type: Boolean, default: false }
  });

  const popover = ref(null);
  const noteValue = ref(props.note);
  const bookmarked = ref(props.bookmarked);
  const userBookmarkId = ref(props.userBookmarkId);
  const targetId = ref(props.targetId);
  const programId = ref(props.programId);
  const isActive = ref(false);

  const iconClasses = computed(() => `fa fa-bookmark ${bookmarked.value ? 'fas' : 'fal'}`);

  const toggle = (event) => {
    isActive.value = !isActive.value;
    popover.value.toggle(event);
  }

  const hide = (event) => { popover.value.hide(event) }

  const deleteBookmark = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(`/my/bookmarks/${userBookmarkId.value}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken,
      },
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then(() => {
      // Update local state
      bookmarked.value = false;
      userBookmarkId.value = '';
      noteValue.value = '';

      hide();
    })
  }

  const updateBookmark = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const commonHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
    };

    let url, method, body;

    if (bookmarked.value && userBookmarkId.value) {
      url = `/my/bookmarks/${userBookmarkId.value}?program_id=${programId.value}&target_id=${targetId.value}`;
      method = 'PATCH';
      body = {
        user_bookmark: {
          notes_attributes: [{ body: noteValue.value }],
        },
      };
    } else {
      url = '/my/bookmarks';
      method = 'POST';
      body = {
        user_bookmark: {
          notes_attributes: [
            { body: noteValue.value }
          ]
        },
        target_id: targetId.value.toString(),
        program_id: programId.value.toString()
      };
    }

    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: commonHeaders,
      body: JSON.stringify(body),
    })
    .then(response => {
      if (!response.ok) throw new Error(`Network response error: ${response.status}`);
      return response.json();
    })
    .then((data) => {
      if (data.object) {
        // Update local state
        bookmarked.value = true;
        userBookmarkId.value = data.object.id;
        
        // Not all bookmarks will have a note
        const noteContainer = document.querySelector(`#user-${targetId.value}-listing .my-notes`);

        if (data.object.note && data.object.note.body) {
          noteValue.value = data.object.note.body;

          if (noteContainer) {
            noteContainer.textContent = data.object.note.body;
          }
        } else {
          if (noteContainer) {
            noteContainer.textContent = '';
          }
        }
      }
      hide();
    })
  };
</script>
